import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyReservations, cancelReservation } from '../services/reservationService';
import { initiatePayment } from '../services/paymentService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import EditReservationModal from '../components/EditReservationModal';
import './MyReservations.css';

const MyReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, upcoming, past
  const [editingReservation, setEditingReservation] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(null); // Track which reservation is processing payment
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to finish loading before checking login status
    if (authLoading) {
      return;
    }
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchReservations();
  }, [isLoggedIn, authLoading, navigate]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await getMyReservations();
      console.log('Reservations response:', response); // Debug log

      // Backend returns: { data: [...], total: number, page: number, limit: number, totalPages: number }
      let reservationsList = [];
      if (Array.isArray(response)) {
        // Direct array
        reservationsList = response;
      } else if (response && Array.isArray(response.data)) {
        // Response with data field (our backend format)
        reservationsList = response.data;
      } else if (response && Array.isArray(response.results)) {
        // Alternative format
        reservationsList = response.results;
      }

      console.log('Parsed reservations list:', reservationsList); // Debug log
      setReservations(reservationsList);
      setError('');
    } catch (err) {
      setError('Failed to load your reservations');
      console.error('Error fetching reservations:', err);
      setReservations([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) {
      return;
    }

    try {
      await cancelReservation(id);
      fetchReservations(); // Refresh the list
    } catch (err) {
      alert('Failed to cancel reservation. Please try again.');
      console.error(err);
    }
  };

  const handleEditReservation = (reservation) => {
    setEditingReservation(reservation);
  };

  const handleEditSuccess = () => {
    setEditingReservation(null);
    fetchReservations(); // Refresh the list
  };

  const handleCloseEditModal = () => {
    setEditingReservation(null);
  };

  // Handle payment retry for pending/failed payments
  const handleRetryPayment = async (reservation) => {
    try {
      setPaymentLoading(reservation.id);
      const response = await initiatePayment(reservation.id);

      if (response.paymentUrl) {
        // Redirect to payment page with payment URL
        const params = new URLSearchParams({
          url: response.paymentUrl,
          reservationId: reservation.id.toString(),
          restaurantName: reservation.restaurant?.name || 'Restaurant',
        });
        navigate(`/payment?${params.toString()}`);
      } else {
        alert('Unable to initiate payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment initiation error:', err);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(null);
    }
  };

  // Get payment status badge class
  const getPaymentBadgeClass = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'paid':
        return 'payment-paid';
      case 'pending':
        return 'payment-pending';
      case 'failed':
        return 'payment-failed';
      case 'cancelled':
        return 'payment-cancelled';
      default:
        return 'payment-default';
    }
  };

  // Get payment status display text
  const getPaymentStatusText = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Payment Pending';
      case 'failed':
        return 'Payment Failed';
      case 'cancelled':
        return 'Payment Cancelled';
      default:
        return paymentStatus;
    }
  };

  // Check if reservation is upcoming (date and time is in future)
  const isUpcomingReservation = (reservation) => {
    const reservationDate = new Date(reservation.date);
    if (reservation.time) {
      const [hours, minutes] = reservation.time.split(':').map(Number);
      reservationDate.setHours(hours || 0, minutes || 0, 0, 0);
    } else {
      reservationDate.setHours(23, 59, 59, 999);
    }
    const now = new Date();
    return reservationDate >= now;
  };

  // Check if reservation can be edited (upcoming + not cancelled/completed/no_show)
  const canEditReservation = (reservation) => {
    const status = reservation.status?.toLowerCase();
    return isUpcomingReservation(reservation) &&
           status !== 'cancelled' &&
           status !== 'completed' &&
           status !== 'no_show';
  };

  const getFilteredReservations = () => {
    // Ensure reservations is always an array
    if (!Array.isArray(reservations)) {
      console.warn('Reservations is not an array:', reservations);
      return [];
    }

    const now = new Date();

    // Helper to get reservation datetime combining date and time
    const getReservationDateTime = (reservation) => {
      const date = new Date(reservation.date);
      if (reservation.time) {
        const [hours, minutes] = reservation.time.split(':').map(Number);
        date.setHours(hours || 0, minutes || 0, 0, 0);
      } else {
        // If no time, set to end of day for past comparison
        date.setHours(23, 59, 59, 999);
      }
      return date;
    };

    if (filter === 'upcoming') {
      return reservations.filter(r => getReservationDateTime(r) >= now);
    } else if (filter === 'past') {
      return reservations.filter(r => getReservationDateTime(r) < now);
    }
    return reservations;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'status-confirmed';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      case 'completed':
        return 'status-completed';
      case 'no_show':
        return 'status-no-show';
      default:
        return 'status-default';
    }
  };

  const filteredReservations = getFilteredReservations();

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="my-reservations-container">
        <Navbar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-reservations-container">
      <Navbar />

      <div className="reservations-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            <i className="bi bi-calendar-check-fill me-3"></i>
            My Reservations
          </h1>
          <p className="hero-subtitle">Manage all your restaurant bookings in one place</p>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#ffffff"/>
          </svg>
        </div>
      </div>

      <div className="container py-5">
        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            <i className="bi bi-list-ul me-2"></i>
            All Reservations
          </button>
          <button
            className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
            onClick={() => setFilter('upcoming')}
          >
            <i className="bi bi-calendar-event me-2"></i>
            Upcoming
          </button>
          <button
            className={`filter-tab ${filter === 'past' ? 'active' : ''}`}
            onClick={() => setFilter('past')}
          >
            <i className="bi bi-clock-history me-2"></i>
            Past
          </button>
        </div>

        {error && (
          <div className="error-alert" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredReservations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="bi bi-calendar-x"></i>
            </div>
            <h3>No Reservations Found</h3>
            <p>
              {filter === 'upcoming'
                ? "You don't have any upcoming reservations"
                : filter === 'past'
                ? "You don't have any past reservations"
                : "You haven't made any reservations yet"}
            </p>
            <button className="btn-explore" onClick={() => navigate('/')}>
              <i className="bi bi-compass me-2"></i>
              Explore Restaurants
            </button>
          </div>
        ) : (
          <div className="reservations-grid">
            {filteredReservations.map((reservation) => (
              <div key={reservation.id} className="reservation-card">
                <div className="card-header">
                  <div className="restaurant-info">
                    <h3 className="restaurant-name">
                      {reservation.restaurant?.name || 'Restaurant'}
                    </h3>
                    {reservation.restaurant?.location && (
                      <p className="restaurant-location">
                        <i className="bi bi-geo-alt-fill me-1"></i>
                        {reservation.restaurant.location}
                      </p>
                    )}
                  </div>
                  <div className="badges-container">
                    <span className={`status-badge ${getStatusBadgeClass(reservation.status)}`}>
                      {reservation.status || 'Pending'}
                    </span>
                    {reservation.requiresPayment && reservation.paymentStatus && (
                      <span className={`payment-badge ${getPaymentBadgeClass(reservation.paymentStatus)}`}>
                        <i className={`bi ${reservation.paymentStatus === 'paid' ? 'bi-check-circle-fill' : reservation.paymentStatus === 'failed' ? 'bi-x-circle-fill' : 'bi-clock-fill'} me-1`}></i>
                        {getPaymentStatusText(reservation.paymentStatus)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  <div className="reservation-details">
                    <div className="detail-item">
                      <i className="bi bi-calendar3"></i>
                      <div className="detail-content">
                        <span className="detail-label">Date</span>
                        <span className="detail-value">
                          {new Date(reservation.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="detail-item">
                      <i className="bi bi-clock"></i>
                      <div className="detail-content">
                        <span className="detail-label">Time</span>
                        <span className="detail-value">{reservation.time}</span>
                      </div>
                    </div>

                    <div className="detail-item">
                      <i className="bi bi-people"></i>
                      <div className="detail-content">
                        <span className="detail-label">Guests</span>
                        <span className="detail-value">
                          {reservation.guests} {reservation.guests === 1 ? 'Guest' : 'Guests'}
                        </span>
                      </div>
                    </div>

                    {reservation.specialOccasion && (
                      <div className="detail-item special">
                        <i className="bi bi-star-fill"></i>
                        <div className="detail-content">
                          <span className="detail-label">Special Occasion</span>
                        </div>
                      </div>
                    )}

                    {reservation.comments && (
                      <div className="detail-item full-width">
                        <i className="bi bi-chat-left-text"></i>
                        <div className="detail-content">
                          <span className="detail-label">Comments</span>
                          <span className="detail-value">{reservation.comments}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-footer">
                  <button
                    className="btn-view"
                    onClick={() => navigate(`/restaurant/${reservation.restaurantId}`)}
                  >
                    <i className="bi bi-eye me-2"></i>
                    View Restaurant
                  </button>
                  {/* Pay Now button for pending/failed payments */}
                  {reservation.requiresPayment &&
                   ['pending', 'failed'].includes(reservation.paymentStatus?.toLowerCase()) &&
                   isUpcomingReservation(reservation) &&
                   reservation.status?.toLowerCase() !== 'cancelled' && (
                    <button
                      className="btn-pay"
                      onClick={() => handleRetryPayment(reservation)}
                      disabled={paymentLoading === reservation.id}
                    >
                      {paymentLoading === reservation.id ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-credit-card me-2"></i>
                          Pay Now
                        </>
                      )}
                    </button>
                  )}
                  {canEditReservation(reservation) && (
                    <button
                      className="btn-edit"
                      onClick={() => handleEditReservation(reservation)}
                    >
                      <i className="bi bi-pencil-square me-2"></i>
                      Edit
                    </button>
                  )}
                  {reservation.status?.toLowerCase() !== 'cancelled' &&
                   reservation.status?.toLowerCase() !== 'completed' && (
                    <button
                      className="btn-cancel"
                      onClick={() => handleCancelReservation(reservation.id)}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Reservation Modal */}
      {editingReservation && (
        <EditReservationModal
          reservation={editingReservation}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default MyReservations;
